import React, { useMemo, useState } from "react";
import { Check, Loader2, RefreshCw, Sparkles } from "lucide-react";

import { runVisualSynthesisCapability } from "../../lib/brandStudioApi";

const GROUP_LABELS = {
  color: "Цвет",
  typography: "Типографика",
  materiality: "Материалы / фактуры",
  photography: "Фотография",
  graphic_language: "Графический язык",
  composition: "Композиция",
  applications: "Носители",
};

const INTERPRETATION_LABELS = {
  user_explicit: "явно выбрано пользователем",
  user_confirmed: "подтверждено пользователем",
  repeated_aligned_evidence: "повторяется в разных источниках",
  system_inferred: "осторожный системный вывод",
  visual_occurrence: "визуально присутствует",
};

function errorText(error) {
  const detail = error?.response?.data?.detail;
  const code =
    typeof detail === "object" && detail
      ? detail.code
      : typeof detail === "string"
        ? detail
        : "";
  const validationSubcode =
    typeof detail === "object" && detail
      ? detail.validation_subcode
      : "";
  const map = {
    synthesis_cached_vision_required:
      "Для VU4.5 нужен хотя бы один уже сохранённый VU3-анализ. Сначала проверь один референс в VU3 Owner QA.",
    synthesis_full_vision_cache_required:
      "Для synthesis нужен полный Vision Cache по всем активным референсам. Сначала заполни visual universe через VU4.6.",
    synthesis_test_already_running: "Synthesis уже выполняется.",
    synthesis_test_already_succeeded:
      "Этот exact input уже успешно проверен. Новый платный вызов заблокирован.",
    synthesis_test_retry_required:
      "Предыдущая попытка завершилась ошибкой. Нажми «Повторить synthesis».",
    synthesis_mistral_not_configured: "Mistral synthesis не настроен на Preview.",
    synthesis_output_invalid: "Mistral вернул ответ не по VisualUnderstanding schema.",
    synthesis_response_incomplete: "Mistral не завершил structured response.",
    synthesis_guard_rejected:
      "Ответ Mistral отклонён VU4.4 guard: evidence hierarchy или provenance нарушены.",
  };
  const base = map[code] || code || "Не удалось выполнить VU4.5 synthesis capability test.";
  return validationSubcode
    ? `${base} Диагностика: ${validationSubcode}`
    : base;
}

function money(micros, currency) {
  if (micros === null || micros === undefined || !currency) return null;
  return `${(Number(micros) / 1_000_000).toFixed(6)} ${currency}`;
}

export default function VisualSynthesisCapabilityOwnerPanelV1() {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(false);
  const [error, setError] = useState("");

  const grouped = useMemo(() => {
    const map = {};
    for (const signal of result?.understanding?.signals || []) {
      if (!map[signal.group]) map[signal.group] = [];
      map[signal.group].push(signal);
    }
    return map;
  }, [result]);

  const run = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const next = await runVisualSynthesisCapability(retry);
      setResult(next);
      setRetry(false);
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail;
      const code =
        typeof detail === "object" && detail ? detail.code : "";
      setRetry(code === "synthesis_test_retry_required");
      setError(errorText(requestError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="rounded-[24px] border border-dashed border-[#8C7359] bg-[#FFF8ED] p-5 md:p-7"
      data-testid="visual-synthesis-capability-owner-panel-v1"
    >
      <div className="text-[9.5px] uppercase tracking-[0.2em] text-[#8A6D53]">
        OWNER QA · VU4.5
      </div>
      <h3 className="mt-2 font-serif text-[27px] leading-tight text-[#2B211A]">
        Проверка: Mistral собирает Visual Understanding
      </h3>
      <p className="mt-2 max-w-3xl text-[12.5px] leading-5 text-[#655141]">
        Это один synthesis-вызов по текущему Source Truth и уже сохранённым VU3-наблюдениям.
        Он не анализирует новые изображения, не вызывает Prodia и не создаёт Visual Story.
      </p>

      <button
        type="button"
        disabled={busy}
        onClick={run}
        className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-full bg-[#3D2A20] px-5 text-[12px] text-[#FFF8ED] disabled:opacity-50"
        data-testid="visual-synthesis-capability-run-v1"
      >
        {busy ? (
          <>
            <Loader2 size={14} className="animate-spin" />
            Собираю understanding…
          </>
        ) : retry ? (
          <>
            <RefreshCw size={14} />
            Повторить synthesis
          </>
        ) : (
          <>
            <Sparkles size={14} />
            Проверить synthesis
          </>
        )}
      </button>

      {error && (
        <div className="mt-5 rounded-[13px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[12px] leading-5 text-[#6B443A]">
          {error}
        </div>
      )}

      {result && (
        <div className="mt-6 border-t border-[#E2D5C3] pt-5" data-testid="visual-synthesis-capability-result-v1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[10.5px] text-[#586044]">
              <Check size={12} /> Synthesis завершён
            </span>
            <span className="text-[10.5px] text-[#8A745E]">
              {result.provider} · {result.model}
            </span>
            <span className="text-[10.5px] text-[#8A745E]">
              · cached refs: {result.cached_reference_count}
            </span>
            {money(result.actual_cost_micros, result.currency) && (
              <span className="text-[10.5px] text-[#8A745E]">
                · {money(result.actual_cost_micros, result.currency)}
              </span>
            )}
          </div>

          <div className="mt-4 rounded-[13px] border border-[#DCCDB8] bg-white px-4 py-3 text-[11px] leading-5 text-[#655141]">
            Результат ниже уже прошёл VU4.4 evidence guard. Явные пользовательские решения
            должны быть сильнее visual frequency, а raw Vision-наблюдения — оставаться weak.
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {Object.entries(grouped).map(([group, signals]) => (
              <article
                key={group}
                className="rounded-[14px] border border-[#DED0BC] bg-white p-4"
              >
                <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">
                  {GROUP_LABELS[group] || group}
                </div>
                <div className="mt-2 space-y-3">
                  {signals.map((signal) => (
                    <div key={signal.id}>
                      <div className="text-[11.5px] leading-5 text-[#4C392B]">
                        • {signal.value}
                      </div>
                      <div className="mt-1 text-[9.5px] leading-4 text-[#8A745E]">
                        {INTERPRETATION_LABELS[signal.interpretation] || signal.interpretation}
                        {" · evidence: "}
                        {signal.evidence?.length || 0}
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>

          {(result.understanding?.rejects || []).length > 0 && (
            <div className="mt-4 rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">
                Чего избегать
              </div>
              <div className="mt-2 space-y-2">
                {result.understanding.rejects.map((item) => (
                  <div key={item.id} className="text-[11.5px] leading-5 text-[#4C392B]">
                    • {item.value}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(result.understanding?.conflicts || []).length > 0 && (
            <div className="mt-4 rounded-[14px] border border-[#D8C8B2] bg-[#FFFCF6] p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">
                Противоречия
              </div>
              <div className="mt-2 space-y-2">
                {result.understanding.conflicts.map((item) => (
                  <div key={item.id} className="text-[11.5px] leading-5 text-[#4C392B]">
                    • {item.description} · {item.status}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 text-[10.5px] leading-5 text-[#8A745E]">
            Вход: {result.input_units || 0} · выход: {result.output_units || 0}.
            Для VU4.5 PASS проверяем evidence hierarchy, provenance и отсутствие придуманных решений.
          </div>
        </div>
      )}
    </section>
  );
}
